import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { CORE_SERVICES, HttpMethods } from '../../../../../services/api';
import { ThunkAPI } from '../../../../rootReducer';
import { basicApiAsyncThunkCreator } from '../../../../../services/apiAsyncThunkCreator';
import { ICurrentDistribution, ISaveCreditPoolDistributionPayload } from './interfaces';
import { selectCurrentDistribution } from './currentDistribution.selectors';
import { showErrorMessage, showSuccessMessage } from '../../../../notifications';
import { fetchCreditsPools } from '../../../creditsPools/creditsPools.thunk';

export interface IFetchCurrentDistributionPayload {
  dealId: string;
  accountId: string;
}

export const fetchCurrentDistribution: AsyncThunk<ICurrentDistribution, IFetchCurrentDistributionPayload, ThunkAPI> = createAsyncThunk(
  'creditPoolDistribution/fetchCurrentDistribution',
  async (payload: IFetchCurrentDistributionPayload, thunk) => {
    try {
      return await basicApiAsyncThunkCreator<ICurrentDistribution, undefined>({
        endpoint: `credit-pool/distribution-deal/${payload.dealId}/company/${payload.accountId}`,
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Get,
        contentType: 'application/json'
      })(undefined, thunk);
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);
export const saveCreditPoolDistribution: AsyncThunk<undefined, string, ThunkAPI> = createAsyncThunk(
  'creditPoolDistribution/save',
  async (accountId: string, thunk) => {
    const state = thunk.getState();
    const currentDistribution = selectCurrentDistribution(state);

    if (!currentDistribution) {
      const message = 'Current distribution is not set';
      showErrorMessage(message);
      return thunk.rejectWithValue(message);
    }

    const data: ISaveCreditPoolDistributionPayload = {
      dealId: currentDistribution.dealId,
      companyId: accountId,
      creditPools: currentDistribution.creditPools?.map((creditPool) => {
        const newCreditPool = { ...creditPool };
        delete newCreditPool.key;
        return newCreditPool;
      })
    };

    try {
      const response = await basicApiAsyncThunkCreator<undefined, ISaveCreditPoolDistributionPayload>({
        endpoint: 'credit-pool/distribute-manage',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);

      thunk.dispatch(showSuccessMessage('Credit pool distribution has been successfully updated'));
      thunk.dispatch(fetchCreditsPools(accountId));

      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);
